import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { required } from '~/app/helpers/validation';

import Loader from '~/app/common/Loader';

import useForm, { UseFormOptionFields } from '~/app/modules/form/useForm';

import { IssueDetails, IssuePayload, Machine, Project } from '~/types';

import { useStoreActions, useStoreState } from '~/store';

import { AddIssueProps } from '../';

import Footer from './Footer';
import Categories from './Categories';
import Details from './Details';

import classes from './style.module.scss';
import { IssueTypeRepaired, IssueTypes, SOURCE_ISSUE_TYPE } from '~/app/data/statuses/issue';
import useDidMountEffect from '~/app/hooks/useDidMountEffect';
import moment from 'moment';
import useAccessControl from '~/app/hooks/useAccessControl';
import { PERMISSION_GROUPS } from '~/accessControl/permissionGroups';


export type FormProps = {
  type: AddIssueProps['type']
  inspectionType?: AddIssueProps['inspectionType']
  onClose(): void
  issueDetails?: IssueDetails
  onSave?: (id: IssueDetails['id']) => void
  sourceTypeId?: AddIssueProps['sourceTypeId']
  machineId?: Machine['id']
  allowAddMore?: boolean
  isWorkOrderChecklist: boolean
}

export type FormComponent = React.FC<FormProps>

const Form: FormComponent = ({ onClose, type, inspectionType, issueDetails, onSave, sourceTypeId, machineId: machineIdProp, allowAddMore, isWorkOrderChecklist }) => {
  const [showDetails, setShowDetails] = useState<boolean>(issueDetails?.id ? true : false);
  const {checkPermission} = useAccessControl();
  const setIssueRegistrationDatePermission = checkPermission(PERMISSION_GROUPS.setIssueRegistrationDate);

  const { id, projectId } = useParams<{ id: Machine['id'], projectId: Project['id'] }>();
  const resolvedMachineId = machineIdProp ?? issueDetails?.machineId ?? id;
  const currentUserId = useStoreState(state => state.user.data.id);
  const displayTypeInIssues = useStoreState(state => state.machine.displayTypeInIssues);
  const isIssueTypeRequired = useStoreState(state => state.machine.isIssueTypeRequired); 

  const categories = useStoreState(state => state.issue.categories);
  const departments = useStoreState(state => state.app.appData.departments);
  const sortingTypes = useStoreState(state => state.issue.sortingTypes);

  const fetchIssueCategories = useStoreActions(actions => actions.issue.fetchIssueCategories);
  const fetchDepartments = useStoreActions(actions => actions.app.appData.fetchDepartments);
  const fetchIssueSortingTypes = useStoreActions(actions => actions.issue.fetchIssueSortingTypes);

  useEffect(() => {
    if (categories.length === 0) {
      fetchIssueCategories();
    }
    if (departments.length === 0) {
      fetchDepartments();
    }
    if (displayTypeInIssues && sortingTypes.length === 0) {
      fetchIssueSortingTypes();
    }
  }, [categories.length, departments.length, displayTypeInIssues, sortingTypes.length, fetchIssueCategories, fetchDepartments, fetchIssueSortingTypes]);

  const sortingTypeName = (issueDetails as any)?.sortingTypeName;

  const initialCategoryId = useMemo(() => {
    if (issueDetails?.categoryId) {
      return Number(issueDetails.categoryId);
    }
    const catName = issueDetails?.categoryName;
    if (catName && categories.length > 0) {
      const match = categories.find(c => c.name?.trim().toLowerCase() === catName.trim().toLowerCase());
      if (match) return match.id;
    }
    return 0;
  }, [issueDetails, categories]);

  const initialCategoryName = useMemo(() => {
    if (issueDetails?.categoryName) {
      return issueDetails.categoryName;
    }
    if (initialCategoryId && categories.length > 0) {
      const match = categories.find(c => c.id === initialCategoryId);
      if (match) return match.name;
    }
    return undefined;
  }, [issueDetails, initialCategoryId, categories]);

  const initialDepartmentId = useMemo(() => {
    if (issueDetails?.issueDepartmentId) {
      return Number(issueDetails.issueDepartmentId);
    }
    if (issueDetails?.departmentId) {
      return Number(issueDetails.departmentId);
    }
    const deptName = issueDetails?.issueDepartmentName || (issueDetails as any)?.departmentName;
    if (deptName && departments.length > 0) {
      const match = departments.find(d => d.name?.trim().toLowerCase() === deptName.trim().toLowerCase());
      if (match) return match.id;
    }
    if (type === 'issue' && departments.length === 1) {
      return departments[0].id;
    }
    return 0;
  }, [issueDetails, departments, type]);

  const initialSortingType = useMemo(() => {
    if (displayTypeInIssues && type === 'issue') {
      if (issueDetails?.sortingType) {
        return Number(issueDetails.sortingType);
      }
      if (sortingTypeName && sortingTypes.length > 0) {
        const match = sortingTypes.find(s => s.name?.trim().toLowerCase() === sortingTypeName.trim().toLowerCase());
        if (match) return match.id;
      }
    }
    return 0;
  }, [displayTypeInIssues, type, issueDetails, sortingTypeName, sortingTypes]);

  const formFields = useMemo(() => {
    const fields: UseFormOptionFields = [
      'issueType',
      'machineId',
      'projectId',
      ['categoryId', { validators: [['required', required]] }],
      'categoryName',
      ['description', { validators: [['required', required]] }],
      'images',
      'files',
      type === 'issue' ? ['departmentId', { validators: [['required', required]] }] : 'departmentId',
      type === 'repair' ? ['performedBy', { validators: [['required', required]] }] : 'performedBy',
      displayTypeInIssues && type === 'issue' ? (isIssueTypeRequired ? ['sortingType', { validators: [['required', required]] }] : 'sortingType') : '',
      ['type', { validators: [['required', required]] }],
      'sourceTypeId',
    ];

    !issueDetails?.id && setIssueRegistrationDatePermission && fields.push(['registrationDate', { validators: [['required', required]] }]);
    !!inspectionType !== undefined && fields.push(['inspectionType', {}]);

    return fields;
  }, [displayTypeInIssues, inspectionType, isIssueTypeRequired, issueDetails?.id, setIssueRegistrationDatePermission, type]);

  const { values, defaultValues, removeForm, setValue } = useForm<IssuePayload>({
    formName: 'addIssue',
    fields: formFields,
    defaultValues: {
      ...!issueDetails?.id && setIssueRegistrationDatePermission && {registrationDate: moment().format('YYYY-MM-DDTHH:mm')},
      issueType: type,
      machineId: Number(resolvedMachineId),
      projectId: issueDetails?.projectId ? Number(issueDetails?.projectId) : projectId,
      departmentId: initialDepartmentId,
      performedBy: type === 'repair' && currentUserId ? currentUserId : 0,
      categoryId: initialCategoryId,
      categoryName: initialCategoryName,
      type: type === 'repair' ? IssueTypeRepaired : (issueDetails?.type !== undefined ? Number(issueDetails.type) : IssueTypes.MINOR),
      sortingType: initialSortingType,
      ...(inspectionType !== undefined && { inspectionType: inspectionType }),
      images: issueDetails?.images ?? [],
      files: issueDetails?.files ?? [],
      description: issueDetails?.description ?? undefined,
      sourceTypeId: sourceTypeId ?? SOURCE_ISSUE_TYPE.FELT_APP,
    }
  });

  const valuesCategoryId = values?.categoryId;
  const valuesCategoryName = values?.categoryName;
  const valuesDepartmentId = values?.departmentId;
  const valuesSortingType = values?.sortingType;

  useEffect(() => {
    if (categories.length === 0) {
      return;
    }
    if (!valuesCategoryId) {
      let catId = issueDetails?.categoryId ? Number(issueDetails.categoryId) : 0;
      const targetName = valuesCategoryName || issueDetails?.categoryName;
      if (!catId && typeof targetName === 'string' && targetName.trim()) {
        const match = categories.find(c => c.name?.trim().toLowerCase() === targetName.trim().toLowerCase());
        if (match) catId = match.id;
      }
      if (catId) {
        setValue('categoryId', catId);
      }
    }
    if (!valuesCategoryName && valuesCategoryId) {
      const match = categories.find(c => c.id === Number(valuesCategoryId));
      if (match) {
        setValue('categoryName', match.name);
      }
    }
  }, [categories, valuesCategoryId, valuesCategoryName, issueDetails, setValue]);

  useEffect(() => {
    if (type !== 'issue' || departments.length === 0) {
      return;
    }
    if (!valuesDepartmentId) {
      let deptId = issueDetails?.issueDepartmentId ? Number(issueDetails.issueDepartmentId) : 0;
      if (!deptId && issueDetails?.departmentId) {
        deptId = Number(issueDetails.departmentId);
      }
      if (!deptId) {
        const deptName = issueDetails?.issueDepartmentName || (issueDetails as any)?.departmentName;
        if (deptName) {
          const match = departments.find(d => d.name?.trim().toLowerCase() === deptName.trim().toLowerCase());
          if (match) deptId = match.id;
        }
      }
      if (!deptId && (!issueDetails?.id || departments.length === 1)) {
        deptId = departments.length === 1 ? departments[0].id : 0;
      }
      if (deptId) {
        setValue('departmentId', deptId);
      }
    }
  }, [departments, type, valuesDepartmentId, issueDetails, setValue]);

  useEffect(() => {
    if (!displayTypeInIssues || type !== 'issue' || sortingTypes.length === 0) {
      return;
    }
    if (!valuesSortingType) {
      let sType = issueDetails?.sortingType ? Number(issueDetails.sortingType) : 0;
      if (!sType) {
        if (sortingTypeName) {
          const match = sortingTypes.find(s => s.name?.trim().toLowerCase() === sortingTypeName.trim().toLowerCase());
          if (match) sType = match.id;
        }
      }
      if (sType) {
        setValue('sortingType', sType);
      }
    }
  }, [sortingTypes, displayTypeInIssues, type, valuesSortingType, issueDetails, sortingTypeName, setValue]);

  const handleAddMore = useCallback(() => {
    if (defaultValues) {
      Object.keys(defaultValues).forEach(key => {
        setValue(key as keyof IssuePayload, defaultValues[key]);
      });
    }
    setShowDetails(false);
  }, [defaultValues, setValue]);

  useDidMountEffect(() => {
    return () => {
      removeForm();
    }
  });

  if (!values) {
    return <Loader size="32"/>;
  }

  return (
    <>
      <div className={classes['container']}>
        {showDetails ? <Details setShowDetails={setShowDetails} type={type} issueDetails={issueDetails} /> : <Categories />}
      </div>

      <Footer onClose={onClose} showDetails={showDetails} setShowDetails={setShowDetails} type={type} issueDetails={issueDetails} defaultFormValues={defaultValues} onSave={onSave} machineId={machineIdProp} onAddMore={handleAddMore} allowAddMore={allowAddMore} isWorkOrderChecklist={isWorkOrderChecklist} />
    </>
  );
};

export default Form
